// Shared stat column definitions, formatters, and types for ESPN-style stats tables.
// Used by MLBBetaPlayers, MLBBetaMyTeam, and PlayerStatsExpand.

export interface SeasonStats {
  games_played: number;
  at_bats: number;
  hits: number;
  runs_scored: number;
  doubles: number;
  triples: number;
  home_runs: number;
  rbi: number;
  walks_drawn: number;
  strikeouts: number;
  stolen_bases: number;
  caught_stealing: number;
  gidp: number;
  pitcher_wins: number;
  pitcher_losses: number;
  pitcher_strikeouts: number;
  saves: number;
  innings_pitched: number;
  earned_runs: number;
  walks_allowed: number;
  home_runs_allowed: number;
  hits_allowed: number;
  is_pitcher: boolean;
  games_started_pitching: number;
  blown_saves: number;
  wild_pitches: number;
  hit_batsmen: number;
  hit_by_pitch: number;
  balks: number;
  passed_balls: number;
  errors: number;
  outfield_assists: number;
  shutouts: number;
  complete_games: number;
  holds: number;
  left_on_base: number;
  sac_flies: number;
}

export interface StatColumn {
  key: string;
  label: string;
  getValue: (stats: SeasonStats | null) => number | string;
  format?: (v: number | string) => string;
  isRate?: boolean;
}

// Derived stat calculators
export const singles = (s: SeasonStats) => s.hits - s.doubles - s.triples - s.home_runs;
export const avg = (s: SeasonStats) => s.at_bats > 0 ? s.hits / s.at_bats : 0;
export const obp = (s: SeasonStats) => {
  const pa = s.at_bats + s.walks_drawn;
  return pa > 0 ? (s.hits + s.walks_drawn) / pa : 0;
};
export const slg = (s: SeasonStats) => {
  if (s.at_bats === 0) return 0;
  const tb = singles(s) + s.doubles * 2 + s.triples * 3 + s.home_runs * 4;
  return tb / s.at_bats;
};
export const totalBases = (s: SeasonStats) => singles(s) + s.doubles * 2 + s.triples * 3 + s.home_runs * 4;
export const outs = (s: SeasonStats) => s.at_bats - s.hits;
export const era = (s: SeasonStats) => s.innings_pitched > 0 ? (s.earned_runs / s.innings_pitched) * 9 : 0;
export const whip = (s: SeasonStats) => s.innings_pitched > 0 ? (s.walks_allowed + (s.hits_allowed || 0)) / s.innings_pitched : 0;

// Formatters
export const fmtInt = (v: number | string) => typeof v === 'number' ? (v === 0 ? '-' : String(Math.round(v))) : String(v);
export const fmtAvg = (v: number | string) => typeof v === 'number' ? (v === 0 ? '-' : v.toFixed(3).replace(/^0/, '')) : String(v);
export const fmtRate = (v: number | string) => typeof v === 'number' ? (v === 0 ? '-' : v.toFixed(2)) : String(v);
export const fmtIP = (v: number | string) => typeof v === 'number' ? (v === 0 ? '-' : v.toFixed(1)) : String(v);
export const fmtPG = (v: number | string) => typeof v === 'number' ? (v === 0 ? '-' : v.toFixed(1)) : String(v);

// Column definitions — My Team hitting widget (every scoring-weighted batting stat)
export const BATTER_COLUMNS: StatColumn[] = [
  { key: 'GP', label: 'GP', getValue: s => s?.games_played ?? 0, format: fmtInt },
  { key: 'AB', label: 'AB', getValue: s => s?.at_bats ?? 0, format: fmtInt },
  { key: 'OUT', label: 'OUT', getValue: s => s ? outs(s) : 0, format: fmtInt },
  { key: 'K', label: 'K', getValue: s => s?.strikeouts ?? 0, format: fmtInt },
  { key: 'GIDP', label: 'GIDP', getValue: s => s?.gidp ?? 0, format: fmtInt },
  { key: 'H', label: 'H', getValue: s => s?.hits ?? 0, format: fmtInt },
  { key: 'TB', label: 'TB', getValue: s => s ? totalBases(s) : 0, format: fmtInt },
  { key: 'HR', label: 'HR', getValue: s => s?.home_runs ?? 0, format: fmtInt },
  { key: 'R', label: 'R', getValue: s => s?.runs_scored ?? 0, format: fmtInt },
  { key: 'RBI', label: 'RBI', getValue: s => s?.rbi ?? 0, format: fmtInt },
  { key: 'BB', label: 'BB', getValue: s => s?.walks_drawn ?? 0, format: fmtInt },
  { key: '2B', label: '2B', getValue: s => s?.doubles ?? 0, format: fmtInt },
  { key: '3B', label: '3B', getValue: s => s?.triples ?? 0, format: fmtInt },
  { key: 'SB', label: 'SB', getValue: s => s?.stolen_bases ?? 0, format: fmtInt },
  { key: 'CS', label: 'CS', getValue: s => s?.caught_stealing ?? 0, format: fmtInt },
  { key: 'HBP', label: 'HBP', getValue: s => s?.hit_by_pitch ?? 0, format: fmtInt },
  { key: 'LOB', label: 'LOB', getValue: s => s?.left_on_base ?? 0, format: fmtInt },
  { key: 'SF', label: 'SF', getValue: s => s?.sac_flies ?? 0, format: fmtInt },
  { key: 'E', label: 'E', getValue: s => s?.errors ?? 0, format: fmtInt },
];

// My Team pitching widget (every scoring-weighted pitching stat)
export const PITCHER_COLUMNS: StatColumn[] = [
  { key: 'GP', label: 'GP', getValue: s => s?.games_played ?? 0, format: fmtInt },
  { key: 'IP', label: 'IP', getValue: s => s?.innings_pitched ?? 0, format: fmtIP },
  { key: 'K', label: 'K', getValue: s => s?.pitcher_strikeouts ?? 0, format: fmtInt },
  { key: 'BB', label: 'BB', getValue: s => s?.walks_allowed ?? 0, format: fmtInt },
  { key: 'ER', label: 'ER', getValue: s => s?.earned_runs ?? 0, format: fmtInt },
  { key: 'HA', label: 'H', getValue: s => s?.hits_allowed ?? 0, format: fmtInt },
  { key: 'HRA', label: 'HR', getValue: s => s?.home_runs_allowed ?? 0, format: fmtInt },
  { key: 'W', label: 'W', getValue: s => s?.pitcher_wins ?? 0, format: fmtInt },
  { key: 'L', label: 'L', getValue: s => s?.pitcher_losses ?? 0, format: fmtInt },
  { key: 'SV', label: 'SV', getValue: s => s?.saves ?? 0, format: fmtInt },
  { key: 'HLD', label: 'HLD', getValue: s => s?.holds ?? 0, format: fmtInt },
  { key: 'BK', label: 'BK', getValue: s => s?.balks ?? 0, format: fmtInt },
  { key: 'PHBP', label: 'HBP', getValue: s => s?.hit_batsmen ?? 0, format: fmtInt },
  { key: 'WP', label: 'WP', getValue: s => s?.wild_pitches ?? 0, format: fmtInt },
  { key: 'CG', label: 'CG', getValue: s => s?.complete_games ?? 0, format: fmtInt },
  { key: 'SHO', label: 'SHO', getValue: s => s?.shutouts ?? 0, format: fmtInt },
];

/** Combined columns for unified roster view — every tracked stat */
export const ALL_COLUMNS: StatColumn[] = [
  { key: 'GP', label: 'GP/GS', getValue: s => s?.is_pitcher ? (s?.games_started_pitching ?? 0) : (s?.games_played ?? 0), format: fmtInt },
  // ── Batting: Trash Stats ──
  { key: 'AB', label: 'AB', getValue: s => s?.at_bats ?? 0, format: fmtInt },
  { key: 'OUT', label: 'OUT', getValue: s => s ? outs(s) : 0, format: fmtInt },
  { key: 'K', label: 'K', getValue: s => s?.strikeouts ?? 0, format: fmtInt },
  { key: 'GIDP', label: 'GIDP', getValue: s => s?.gidp ?? 0, format: fmtInt },
  // ── Batting: Anti-Stats ──
  { key: 'H', label: 'H', getValue: s => s?.hits ?? 0, format: fmtInt },
  { key: 'TB', label: 'TB', getValue: s => s ? totalBases(s) : 0, format: fmtInt },
  { key: 'HR', label: 'HR', getValue: s => s?.home_runs ?? 0, format: fmtInt },
  { key: 'R', label: 'R', getValue: s => s?.runs_scored ?? 0, format: fmtInt },
  { key: 'RBI', label: 'RBI', getValue: s => s?.rbi ?? 0, format: fmtInt },
  { key: 'BB', label: 'BB', getValue: s => s?.walks_drawn ?? 0, format: fmtInt },
  { key: '2B', label: '2B', getValue: s => s?.doubles ?? 0, format: fmtInt },
  { key: '3B', label: '3B', getValue: s => s?.triples ?? 0, format: fmtInt },
  // ── Pitching: Trash Stats ──
  { key: 'PBB', label: 'PBB', getValue: s => s?.walks_allowed ?? 0, format: fmtInt },
  { key: 'HRA', label: 'HRA', getValue: s => s?.home_runs_allowed ?? 0, format: fmtInt },
  { key: 'ER', label: 'ER', getValue: s => s?.earned_runs ?? 0, format: fmtInt },
  { key: 'BS', label: 'BS', getValue: s => s?.blown_saves ?? 0, format: fmtInt },
  { key: 'HBP', label: 'HBP', getValue: s => s?.hit_batsmen ?? 0, format: fmtInt },
  { key: 'WP', label: 'WP', getValue: s => s?.wild_pitches ?? 0, format: fmtInt },
  { key: 'BK', label: 'BK', getValue: s => s?.balks ?? 0, format: fmtInt },
  // ── Pitching: Anti-Stats ──
  { key: 'IP', label: 'IP', getValue: s => s?.innings_pitched ?? 0, format: fmtIP },
  { key: 'PK', label: 'PK', getValue: s => s?.pitcher_strikeouts ?? 0, format: fmtInt },
  { key: 'W', label: 'W', getValue: s => s?.pitcher_wins ?? 0, format: fmtInt },
  { key: 'SV', label: 'SV', getValue: s => s?.saves ?? 0, format: fmtInt },
  { key: 'CG', label: 'CG', getValue: s => s?.complete_games ?? 0, format: fmtInt },
  { key: 'SHO', label: 'SHO', getValue: s => s?.shutouts ?? 0, format: fmtInt },
  { key: 'ERA', label: 'ERA', getValue: s => s ? era(s) : 0, format: fmtRate, isRate: true },
  { key: 'WHIP', label: 'WHIP', getValue: s => s ? whip(s) : 0, format: fmtRate, isRate: true },
  // ── Fielding ──
  { key: 'E', label: 'E', getValue: s => s?.errors ?? 0, format: fmtInt },
  { key: 'PB', label: 'PB', getValue: s => s?.passed_balls ?? 0, format: fmtInt },
  // ── Baserunning ──
  { key: 'CS', label: 'CS', getValue: s => s?.caught_stealing ?? 0, format: fmtInt },
  { key: 'SB', label: 'SB', getValue: s => s?.stolen_bases ?? 0, format: fmtInt },
];

export const PITCHER_POSITIONS = ['SP', 'RP', 'P'];

export function getStatDisplay(col: StatColumn, stats: SeasonStats | null, statView: 'season' | 'pergame'): string {
  const raw = col.getValue(stats);
  if (typeof raw === 'string') return raw;
  if (statView === 'pergame' && !col.isRate && col.key !== 'GP') {
    const gp = stats?.games_played || 0;
    if (gp === 0) return '-';
    return fmtPG(raw / gp);
  }
  return (col.format || fmtInt)(raw);
}

export function getSabPtsDisplay(pts: number | undefined | null, statView: 'season' | 'pergame', gamesPlayed: number): string {
  if (pts == null) return '-';
  if (statView === 'pergame' && gamesPlayed > 0) {
    return (pts / gamesPlayed).toFixed(1);
  }
  if (statView === 'pergame' && gamesPlayed === 0) return '-';
  return pts.toFixed(1);
}
